import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useMemo, useState } from 'react';
import Cards from 'src/components/Cards';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import { ThreeWayMatchingPermissions } from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import usePermission from 'src/hooks/usePermissions';
import useWindowSize from 'src/hooks/useWindowSize';
import useApproveInvoiceApi from 'src/services/useApproveInvoiceApi';
import useCompleteInvoiceApi from 'src/services/useCompleteInvoiceApi';
import { useContextSelector } from 'use-context-selector';
import Checkbox from '../../components/design-system/Checkbox';
import Dialog from '../../components/design-system/Dialog';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

const Complete = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const hasDuplicateInvoiceNumber = useContextSelector(
    InvoiceContext,
    (value) => value.hasDuplicateInvoiceNumber
  );
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);
  const linkedReceiptQuantity = useContextSelector(
    InvoiceContext,
    (value) => value.linkedReceiptQuantity
  );
  const getMessages = useContextSelector(InvoiceContext, (value) => value.getMessages);
  const isInvoiceActionButtonDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceActionButtonDisabled
  );

  const { approveInvoice, approveCallLoading } = useApproveInvoiceApi();
  const { completeInvoice, completeCallLoading } = useCompleteInvoiceApi();
  const { width } = useWindowSize();
  const [initialCheckBox, setInitialCheckBox] = useState(false);
  const { value: open, setTrue: openModal, setFalse: closeModal } = useBoolean();

  const isThreeWayMatching: boolean = usePermission({ permissions: ThreeWayMatchingPermissions });
  const { currentDocQty } = invoice;
  const [completeButtonMessage, completeInvoiceButtonState] = useInvoiceButtonState('Complete');
  const [approveButtonMessage, approveInvoiceButtonState] = useInvoiceButtonState('Approve');
  const [btnContent, setBtnContent] = useState<string | null>('Complete');

  const handleComplete = useCallback(async () => {
    try {
      btnContent === 'Approval'
        ? await approveInvoice({ invoiceIds: [invoiceId] })
        : await completeInvoice({ invoiceIds: [invoiceId] });
    } catch (error) {
      console.error(error);
    } finally {
      closeModal();
      refreshPage();
    }
  }, [approveInvoice, btnContent, closeModal, completeInvoice, invoiceId, refreshPage]);

  let isRecQtyLessDocQty = useMemo(
    () =>
      isThreeWayMatching && linkedReceiptQuantity !== null && linkedReceiptQuantity < currentDocQty,
    [currentDocQty, isThreeWayMatching, linkedReceiptQuantity]
  );

  const isDisableConfirm = useMemo(
    () => hasDuplicateInvoiceNumber && !initialCheckBox,
    [hasDuplicateInvoiceNumber, initialCheckBox]
  );

  const handleCompleteApproval = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setBtnContent(event.currentTarget.textContent === 'Approve' ? 'Approval' : 'Complete');
      openModal();
    },
    [openModal]
  );

  const closeModalPopup = useCallback(() => {
    closeModal();
  }, [closeModal]);

  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px] max-h-[250px] overflow-auto">
            <PopUpContentWrapper
              type="Error"
              contents={[...getMessages(completeInvoiceButtonState, completeButtonMessage)]}
            />
          </div>
        }
        show={isInvoiceActionButtonDisabled(completeInvoiceButtonState)}
      >
        <Button
          buttonState={completeInvoiceButtonState}
          onClick={handleCompleteApproval}
          color="success"
          disabled={isInvoiceActionButtonDisabled(completeInvoiceButtonState)}
          loading={completeCallLoading || approveCallLoading}
        >
          Complete
        </Button>
      </PopUp>
      <PopUp
        content={
          <PopUpContentWrapper
            type="Error"
            contents={[...getMessages(approveInvoiceButtonState, approveButtonMessage)]}
          />
        }
        show={isInvoiceActionButtonDisabled(approveInvoiceButtonState)}
      >
        <Button
          buttonState={approveInvoiceButtonState}
          onClick={handleCompleteApproval}
          color="success"
          disabled={isInvoiceActionButtonDisabled(approveInvoiceButtonState)}
        >
          Approve
        </Button>
      </PopUp>
      <Dialog
        width={width > 1480 ? '50vw' : '800px'}
        onClose={closeModalPopup}
        open={open}
        title={`Confirm ${btnContent}`}
        id="modal"
      >
        <div className="px-2">
          {isRecQtyLessDocQty && (
            <div className="mb-3">
              <p>
                You are about to Complete a document containing line items that exceed the amount
                received. Payment will be processed for items not received.
              </p>
              <p className="mt-1">
                You will not be able to edit the document once you perform this action.
              </p>
              <p className="mt-1">Do you wish to continue?</p>
            </div>
          )}
          {hasDuplicateInvoiceNumber && (
            <Cards title="Warning" styleType="warning">
              <p>The document number provided is a duplicate document number</p>
              <p>
                Please check the checkbox below to complete the document with a duplicate document
                number.
              </p>
              <div className="warning-checkbox">
                <Checkbox
                  label="Allow duplicate document number"
                  checked={initialCheckBox}
                  onChange={setInitialCheckBox}
                />
              </div>
            </Cards>
          )}
          {!isRecQtyLessDocQty && !hasDuplicateInvoiceNumber && (
            <p>
              This document will be completed. You will not be able to edit the document once you
              perform this action. Do you wish to continue?
            </p>
          )}
        </div>
        <DialogActionsBar>
          <div className="flex items-center justify-end gap-2">
            <Button
              color="info"
              disabled={isDisableConfirm}
              loading={completeCallLoading || approveCallLoading}
              onClick={handleComplete}
            >
              Confirm
            </Button>
            <Button color="info" onClick={closeModalPopup}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default Complete;
